import { useState } from 'react';
import { Button, useToast } from '../../components/ui';
import {
  useCatalogStatus,
  useDisconnectCatalog,
  useSetCatalogFallbackImage,
  useSyncCatalog,
} from '../../api/whatsapp-catalog';
import { ProductImageField } from '../products-services/ProductImageField';
import { ChooseCatalogModal, TakeoverModal } from './CatalogModals';

/**
 * The channel's Meta Commerce catalog: choose and connect one of the catalogs
 * linked to the WhatsApp account (turns on the cart), sync counts and last
 * sync, "Sync now", a Commerce Manager link, and taking over items that were
 * already in the catalog. Products sync on their own when saved; this is for
 * the first sync and retries.
 */
export function CatalogStatusRow({
  channelId,
  canManage,
}: {
  channelId: string;
  canManage: boolean;
}) {
  const status = useCatalogStatus(channelId);
  const sync = useSyncCatalog(channelId);
  const disconnect = useDisconnectCatalog(channelId);
  const setFallback = useSetCatalogFallbackImage(channelId);
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<'choose' | 'takeover' | null>(null);
  const busy = sync.isPending || disconnect.isPending || setFallback.isPending;

  async function changeFallback(assetId: string | null) {
    setError(null);
    try {
      const s = await setFallback.mutateAsync(assetId);
      toast.success(
        assetId
          ? `Fallback photo saved - ${s.counts.synced} product(s) synced.`
          : 'Fallback photo removed.',
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save the fallback photo.';
      setError(message);
      toast.error(message);
    }
  }

  async function run(action: typeof sync, done: string) {
    setError(null);
    try {
      const s = await action.mutateAsync();
      toast.success(
        s.counts.failed > 0 ? `${done} ${s.counts.failed} product(s) need attention.` : done,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong.';
      setError(message);
      toast.error(message);
    }
  }

  if (status.isPending) return <span style={{ color: 'var(--color-text-muted)' }}>Loading…</span>;
  if (status.isError)
    return <span style={{ color: 'var(--color-danger)' }}>{status.error.message}</span>;
  const s = status.data;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)', justifyItems: 'start' }}>
      {s.catalogId ? (
        <span>
          <strong>{s.catalogName ?? 'Catalog'}</strong>{' '}
          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            ({s.catalogId}) · {s.counts.synced} product(s) synced
            {s.counts.failed ? ` · ${s.counts.failed} need attention (see Products)` : ''}
            {s.lastSyncedAt
              ? ` · Last synced ${new Date(s.lastSyncedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`
              : ''}
            {s.commerceManagerUrl ? (
              <>
                {' · '}
                <a href={s.commerceManagerUrl} target="_blank" rel="noreferrer">
                  View in Commerce Manager
                </a>
              </>
            ) : null}
          </span>
        </span>
      ) : (
        <span style={{ color: 'var(--color-text-muted)' }}>
          Not connected. Link your Commerce Manager catalog to this number in WhatsApp Manager →
          Catalog, then connect it here to send products with photos and a cart.
        </span>
      )}
      {s.catalogId && canManage ? (
        <ProductImageField
          label="Fallback photo"
          hint="Meta needs a photo for every catalog item. Products without their own photo use this one (e.g. your logo) until you add theirs."
          value={s.fallbackImageAssetId}
          disabled={busy}
          onChange={(assetId) => void changeFallback(assetId)}
        />
      ) : null}
      {error ? <span style={{ color: 'var(--color-danger)' }}>{error}</span> : null}
      {canManage ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {s.catalogId ? (
            <>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => void run(sync, 'Catalog synced.')}
              >
                {sync.isPending ? 'Syncing…' : 'Sync now'}
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => setModal('takeover')}>
                Take over existing items
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => setModal('choose')}>
                Switch catalog
              </Button>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => void run(disconnect, 'Catalog disconnected.')}
              >
                Disconnect catalog
              </Button>
            </>
          ) : (
            <Button variant="secondary" disabled={busy} onClick={() => setModal('choose')}>
              Connect catalog
            </Button>
          )}
        </div>
      ) : null}
      {modal === 'choose' ? (
        <ChooseCatalogModal channelId={channelId} onClose={() => setModal(null)} />
      ) : null}
      {modal === 'takeover' ? (
        <TakeoverModal channelId={channelId} onClose={() => setModal(null)} />
      ) : null}
    </div>
  );
}
